import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { supabase } from '@/lib/supabaseClient';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import StatusBadge from '@/components/base/StatusBadge';

type Tenant = {
  id: string;
  name: string;
  slug: string;
  plan_code: string;
  status: string;
  created_at: string;
};

type DashData = {
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  totalClients: number;
  totalContracts: number;
  totalPayments: number;
  totalJobs: number;
  planBreakdown: Record<string, number>;
  recentTenants: Tenant[];
};

const PLAN_STYLES: Record<string, { badge: string; bar: string }> = {
  free:       { badge: 'bg-slate-100 text-slate-500',      bar: 'bg-slate-300' },
  pro:        { badge: 'bg-amber-50 text-amber-700',       bar: 'bg-amber-400' },
  enterprise: { badge: 'bg-emerald-50 text-emerald-700',   bar: 'bg-emerald-500' },
};

export default function SADashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashData>({
    totalTenants: 0, activeTenants: 0, totalUsers: 0, totalClients: 0,
    totalContracts: 0, totalPayments: 0, totalJobs: 0, planBreakdown: {}, recentTenants: [],
  });

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [activeTenantsRes, allTenantsRes, usersRes, clientsRes, contractsRes, paymentsRes, jobsRes] = await Promise.all([
        supabase.from('saas_tenants').select('id', { count: 'exact' }).eq('status', 'active'),
        supabase.from('saas_tenants').select('id, name, slug, plan_code, status, created_at').order('created_at', { ascending: false }),
        supabase.from('saas_tenant_users').select('id', { count: 'exact' }),
        supabase.from('clients').select('id', { count: 'exact' }),
        supabase.from('contracts').select('id', { count: 'exact' }),
        supabase.from('payments').select('amount, status'),
        supabase.from('jobs').select('id', { count: 'exact' }),
      ]);

      const tenants = (allTenantsRes.data ?? []) as Tenant[];
      const payments = (paymentsRes.data ?? []) as { amount: number; status: string }[];
      const totalPayments = payments.reduce((s, p) => s + Number(p.amount), 0);
      const planBreakdown = tenants.reduce<Record<string, number>>((acc, t) => {
        acc[t.plan_code] = (acc[t.plan_code] ?? 0) + 1;
        return acc;
      }, {});

      setData({
        totalTenants: tenants.length,
        activeTenants: activeTenantsRes.count ?? 0,
        totalUsers: usersRes.count ?? 0,
        totalClients: clientsRes.count ?? 0,
        totalContracts: contractsRes.count ?? 0,
        totalPayments,
        totalJobs: jobsRes.count ?? 0,
        planBreakdown,
        recentTenants: tenants.slice(0, 8),
      });
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error loading dashboard.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/admin'); return; }
      await fetchData();
    };
    init();
  }, [navigate, fetchData]);

  const kpis = [
    { label: 'Total Tenants',   value: data.totalTenants,                          sub: `${data.activeTenants} activos`,          icon: 'ri-building-2-line',   color: 'text-blue-600',    bg: 'bg-blue-50', border: 'border-blue-100' },
    { label: 'Active Tenants',  value: data.activeTenants,                         sub: `${data.totalTenants - data.activeTenants} inactivos`, icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    { label: 'Total Users',     value: data.totalUsers,                            sub: 'En todos los tenants',                   icon: 'ri-team-line',         color: 'text-violet-600',  bg: 'bg-violet-50', border: 'border-slate-200' },
    { label: 'Total Clients',   value: data.totalClients,                          sub: 'Clientes registrados',                   icon: 'ri-user-3-line',       color: 'text-sky-600',     bg: 'bg-sky-50',  border: 'border-slate-200' },
    { label: 'Total Contracts', value: data.totalContracts,                        sub: 'Contratos creados',                      icon: 'ri-file-text-line',    color: 'text-amber-600',   bg: 'bg-amber-50', border: 'border-slate-200' },
    { label: 'Total Payments',  value: `$${data.totalPayments.toLocaleString()}`,  sub: 'Volumen bruto global',                   icon: 'ri-coins-line',        color: 'text-amber-600',   bg: 'bg-amber-50', border: 'border-amber-100' },
    { label: 'Total Jobs',      value: data.totalJobs,                             sub: 'Sesiones registradas',                   icon: 'ri-camera-3-line',     color: 'text-rose-600',    bg: 'bg-rose-50', border: 'border-slate-200' },
  ];

  const planOrder = ['free', 'pro', 'enterprise'];
  const maxPlanCount = Math.max(...planOrder.map((p) => data.planBreakdown[p] ?? 0), 1);

  return (
    <SuperAdminLayout>
      <SAHeader title="Global Dashboard" subtitle="Vista global del sistema Pixory AI — solo lectura" />
      <div className="px-4 md:px-8 py-4 md:py-7 space-y-6">
        {loading ? (
          <LoadingState text="Loading system data…" />
        ) : error ? (
          <ErrorState title="Failed to load dashboard" message={error} onRetry={() => fetchData()} showLogin />
        ) : (
          <>
            {/* KPIs */}
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                {kpis.slice(0, 4).map((k) => (
                  <div key={k.label} className={`bg-white rounded-xl p-5 border ${k.border}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider leading-tight">{k.label}</span>
                      <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${k.bg} ${k.color} shrink-0`}>
                        <i className={`${k.icon} text-sm`}></i>
                      </span>
                    </div>
                    <p className={`text-3xl font-black ${k.color}`}>{k.value}</p>
                    <p className="text-slate-400 text-xs mt-1.5">{k.sub}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                {kpis.slice(4).map((k) => (
                  <div key={k.label} className={`bg-white rounded-xl p-5 border ${k.border}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider leading-tight">{k.label}</span>
                      <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${k.bg} ${k.color} shrink-0`}>
                        <i className={`${k.icon} text-sm`}></i>
                      </span>
                    </div>
                    <p className={`text-3xl font-black ${k.color}`}>{k.value}</p>
                    <p className="text-slate-400 text-xs mt-1.5">{k.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Plan distribution + Recent tenants */}
            <div className="grid grid-cols-3 gap-6">
              {/* Plan breakdown */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-slate-900 font-semibold text-sm mb-5">Distribución de planes</h3>
                <div className="space-y-4">
                  {planOrder.map((plan) => {
                    const count = data.planBreakdown[plan] ?? 0;
                    const pct = data.totalTenants > 0 ? Math.round((count / data.totalTenants) * 100) : 0;
                    const s = PLAN_STYLES[plan];
                    return (
                      <div key={plan}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${s.badge}`}>{plan}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-900 text-sm font-bold">{count}</span>
                            <span className="text-slate-400 text-xs w-8 text-right">{pct}%</span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full transition-all ${s.bar}`} style={{ width: `${Math.round((count / maxPlanCount) * 100)}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                  {data.totalTenants === 0 && <p className="text-slate-400 text-sm text-center py-4">Sin tenants todavía</p>}
                </div>

                {/* System health */}
                <div className="mt-6 pt-5 border-t border-slate-100 space-y-2">
                  <p className="text-slate-500 text-xs uppercase tracking-wider mb-3">Estado del sistema</p>
                  {[
                    { label: 'RLS activo',           ok: true },
                    { label: 'Super Admin isolado',  ok: true },
                    { label: 'Queries globales',     ok: true },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className={`w-4 h-4 flex items-center justify-center ${item.ok ? 'text-emerald-500' : 'text-red-500'}`}>
                        <i className={`${item.ok ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} text-xs`}></i>
                      </span>
                      <span className="text-slate-500 text-xs">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent tenants */}
              <div className="col-span-2 bg-white rounded-xl border border-slate-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-slate-900 font-semibold text-sm">Tenants recientes</h3>
                  <button onClick={() => navigate('/sa/tenants')} className="text-blue-600 text-xs hover:underline cursor-pointer whitespace-nowrap">
                    Ver todos →
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {data.recentTenants.length === 0 ? (
                    <p className="px-5 py-10 text-slate-400 text-sm text-center">Sin tenants registrados</p>
                  ) : data.recentTenants.map((t) => (
                    <div key={t.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 text-xs font-bold shrink-0">
                          {t.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-slate-900 text-sm font-medium truncate">{t.name}</p>
                          <p className="text-slate-400 text-xs font-mono">{t.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${PLAN_STYLES[t.plan_code]?.badge ?? 'bg-slate-100 text-slate-400'}`}>
                          {t.plan_code}
                        </span>
                        <StatusBadge status={t.status} size="xs" />
                        <span className="text-slate-300 text-xs">
                          {new Date(t.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}
